import { useCallback, useEffect, useRef, useState } from "react";
import { shotAlt, shotAt, stepTo, type Reel } from "../lib/flyerViewer";
import { useApp } from "./appState";

/** How far a touch has to travel before it counts as a swipe and not a tap. */
const SWIPE = 44;

/**
 * A flyer at full size over whatever opened it. The thumbnail already loaded
 * this exact URL, so there is nothing more to fetch. It opens fit to the
 * screen; a tap shows it at its own pixels, to read the small print on a
 * dense flyer. Arrows, the arrow keys and a swipe step through the reel the
 * flyer came from (Matt's pick: tap the flyer, step within the view).
 */
export function FlyerViewer({ reel, onClose }: { reel: Reel; onClose: () => void }) {
  const { open } = useApp();
  const [at, setAt] = useState(reel.at);
  const [actual, setActual] = useState(false);
  const [natural, setNatural] = useState<number | null>(null);
  const closeRef = useRef<HTMLButtonElement>(null);
  const touchX = useRef<number | null>(null);

  const count = reel.shots.length;
  const many = count > 1;
  const shot = shotAt({ shots: reel.shots, at });

  const step = useCallback(
    (by: number) => {
      setAt((i) => stepTo(i, by, count));
      setActual(false);
      setNatural(null);
    },
    [count],
  );

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
      else if (e.key === "ArrowLeft" && many) step(-1);
      else if (e.key === "ArrowRight" && many) step(1);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose, step, many]);

  // The page behind must not scroll while a flyer is over it, and focus starts
  // on Close so a keyboard can leave straight away.
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    closeRef.current?.focus();
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);

  if (!shot) return null;

  const details = shot.sheet;
  return (
    <div className="lightbox" role="dialog" aria-modal="true" aria-label={`Flyer: ${shot.title}`} data-testid="flyer-viewer">
      <div className="lb-bar">
        <div className="lb-who">
          <div className="lb-t">{shot.title}</div>
          {shot.sub && <div className="lb-s mono">{shot.sub}</div>}
        </div>
        <button type="button" ref={closeRef} className="icon-btn lb-x" onClick={onClose} aria-label="Close">
          ✕
        </button>
      </div>
      <div
        className={`lb-stage${actual ? " actual" : ""}`}
        onClick={(e) => {
          if (e.target === e.currentTarget) onClose();
        }}
        // A pinch to zoom the flyer is two fingers, and one of them can travel
        // far enough sideways to read as a swipe. Only a single finger, down
        // and up on its own, steps the reel.
        onTouchStart={(e) => (touchX.current = e.touches.length === 1 ? (e.touches[0]?.clientX ?? null) : null)}
        onTouchEnd={(e) => {
          const from = touchX.current;
          touchX.current = null;
          const to = e.changedTouches[0]?.clientX;
          if (from === null || to === undefined || !many || e.touches.length > 0) return;
          if (Math.abs(to - from) >= SWIPE) step(to < from ? 1 : -1);
        }}
      >
        {many && (
          <button type="button" className="lb-nav prev" onClick={() => step(-1)} aria-label="Previous flyer">
            ‹
          </button>
        )}
        <img
          src={shot.url}
          alt={shotAlt(shot)}
          decoding="async"
          style={actual && natural ? { width: natural } : undefined}
          onLoad={(e) => setNatural(e.currentTarget.naturalWidth || null)}
          onClick={() => setActual((a) => !a)}
        />
        {many && (
          <button type="button" className="lb-nav next" onClick={() => step(1)} aria-label="Next flyer">
            ›
          </button>
        )}
      </div>
      <div className="lb-foot">
        {details && (
          <button
            type="button"
            className="btn sm"
            onClick={() => {
              onClose();
              open(details.kind, details.value);
            }}
          >
            Show details
          </button>
        )}
        {/* The flyer's own object, for saving it or reading it in another tab. */}
        <a className="btn ghost sm" href={shot.url} target="_blank" rel="noopener noreferrer">
          Open original
        </a>
        <span className="lb-hint">{actual ? "Actual size · tap to fit" : "Tap the flyer for actual size"}</span>
        {many && (
          <span className="lb-of mono">
            {at + 1} of {count}
          </span>
        )}
      </div>
    </div>
  );
}
